import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PublicNav from '@/components/PublicNav';
import WorldMap from '@/components/giraffe/WorldMap';
import Logotype from '@/components/shared/Logotype';
import ZLoadingBar from '@/components/shared/ZLoadingBar';
import ZEmptyState from '@/components/shared/ZEmptyState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate) return '';
  const start = new Date(startDate);
  const startStr = start.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  if (!endDate) return startStr;
  const end = new Date(endDate);
  const endStr = end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  return `${startStr} - ${endStr}`;
};

function CountriesCard({ trip, onClick }) {
  const visitedCodes = useMemo(() => new Set(trip.visited_codes || []), [trip.visited_codes]);
  const visitCounts = useMemo(() => trip.visit_counts || {}, [trip.visit_counts]);

  return (
    <div
      onClick={onClick}
      className="group cursor-pointer overflow-hidden rounded-lg bg-white/[0.03] border border-white/[0.04] hover:border-white/[0.06] transition-all"
      data-testid="travel-card-countries"
    >
      <div className="aspect-[4/3] relative overflow-hidden bg-white/[0.03]">
        <div style={{ transform: 'scale(1.18) translateY(-6%)', transformOrigin: '50% 42%', pointerEvents: 'none' }}>
          <WorldMap visitedCodes={visitedCodes} visitCounts={visitCounts} />
        </div>
        {/* Country count overlay */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="font-black" style={{ fontFamily: "'Playfair Display', serif", fontSize: '3.5rem', color: '#10B981', opacity: 0.9, textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}>
            {trip.total_countries}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-lg text-[#f0f0f5] font-medium leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          {trip.title}
        </h3>
        <p className="font-mono text-xs text-[#b0b4c0]/70 mt-1">The world so far</p>
      </div>
    </div>
  );
}

function PostcardsCard({ trip, onClick }) {
  return (
    <div
      onClick={onClick}
      className="group cursor-pointer overflow-hidden rounded-lg bg-white/[0.03] border border-white/[0.04] hover:border-white/[0.06] transition-all"
      data-testid="travel-card-postcards"
    >
      <div className="aspect-[4/3] relative overflow-hidden bg-white/[0.03]">
        {trip.cover_image ? (
          <img
            src={getImageUrl(trip.cover_image)}
            alt="Postcards"
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#10B981]/15 via-[#0a0a0f] to-[#0a0a0f]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f]/80 via-transparent to-transparent" />
        <div className="absolute bottom-3 right-4 pointer-events-none">
          <span className="font-black" style={{ fontFamily: "'Playfair Display', serif", fontSize: '2.5rem', color: '#10B981', opacity: 0.9, textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}>
            {trip.count}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-lg text-[#f0f0f5] font-medium leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          {trip.title}
        </h3>
        <p className="font-mono text-xs text-[#b0b4c0]/70 mt-1">Notes from the road</p>
      </div>
    </div>
  );
}

function TripCard({ trip, onClick }) {
  const [loaded, setLoaded] = useState(false);
  const hasImage = !!trip.cover_image;
  const isLive = trip.trip_type === 'live';

  return (
    <div
      onClick={onClick}
      className="group cursor-pointer overflow-hidden rounded-lg bg-white/[0.02] border border-white/[0.04] hover:border-white/[0.06] transition-all"
      data-testid={`travel-card-${trip.id}`}
    >
      <div className="aspect-[4/3] relative overflow-hidden">
        {hasImage ? (
          <>
            {!loaded && (
              <div className="absolute inset-0 bg-white/[0.05] flex items-center justify-center">
                <ZLoadingBar accent="#10B981" loading={true} />
              </div>
            )}
            <img
              src={getImageUrl(trip.cover_image)}
              alt={trip.title}
              loading="lazy"
              onLoad={() => setLoaded(true)}
              className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${loaded ? 'opacity-100' : 'opacity-0'}`}
            />
          </>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-white/[0.05] via-[#0a0a0f] to-[#0a0a0f]" />
        )}
        {/* Live badge */}
        {isLive && (
          <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-[#0a0a0f]/70 backdrop-blur-sm px-2 py-1 rounded-full">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
            </span>
            <span className="text-[10px] font-mono text-emerald-400 tracking-wider uppercase">Live</span>
          </div>
        )}
        {/* Lock icon */}
        {trip.password_protected && (
          <div className="absolute top-3 right-3 bg-[#0a0a0f]/70 backdrop-blur-sm p-1.5 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#b0b4c0]">
              <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
        )}
      </div>

      <div className="p-4">
        <h3
          className="text-lg text-[#f0f0f5] font-medium leading-tight"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {trip.title}
        </h3>
        <p className="font-mono text-xs text-[#b0b4c0]/70 mt-1">
          {formatDateRange(trip.start_date, trip.end_date)}
        </p>
      </div>
    </div>
  );
}

export default function TravelsGallery() {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTravels();
  }, []);

  const loadTravels = async () => {
    try {
      const { data } = await axios.get(`${API}/public/travels`);
      setTrips(data || []);
    } catch (err) {
      console.error('Failed to load travels:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <ZLoadingBar accent="#10B981" loading={true} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-4 md:pt-16 pb-20" data-testid="travels-gallery-page">
      <PublicNav />

      {/* Header */}
      <header className="px-6 md:px-12 pt-6 md:pt-8 pb-6">
        <div className="max-w-[1000px] mx-auto flex items-end justify-between gap-4">
          <div>
            <div className="flex items-center mb-1">
              <Logotype app="giraffe" size="header" />
            </div>
            <p className="text-xs tracking-[0.2em] uppercase" style={{ fontFamily: "'JetBrains Mono', monospace", color: '#b0b4c0', paddingLeft: 42 }}>
              Journeys · The World Unraveled
            </p>
          </div>
          <a href="/labs" className="flex md:hidden items-center gap-2" style={{ textDecoration: 'none', opacity: 0.45 }}>
            <Logotype app="zephyr" size="sidebar" />
          </a>
        </div>
      </header>

      {/* Grid */}
      <div className="max-w-[1000px] mx-auto px-4 md:px-8">
        {trips.length === 0 ? (
          <div data-testid="travels-empty">
            <ZEmptyState mark="G" accent="#10B981" message="No memoirs published yet" />
          </div>
        ) : (
          <div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 pb-12"
            data-testid="travels-grid"
          >
            {trips.map((trip) => {
              if (trip.trip_type === 'countries') {
                return <CountriesCard key="countries" trip={trip} onClick={() => navigate('/travels/countries')} />;
              }
              if (trip.trip_type === 'postcards') {
                return <PostcardsCard key="postcards" trip={trip} onClick={() => navigate('/travels/postcards')} />;
              }
              return (
                <TripCard
                  key={trip.id}
                  trip={trip}
                  onClick={() => navigate(`/trip/${trip.public_slug}/memoir`)}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-12 flex items-center justify-center gap-1">
        <img src="/logo-zephyr.png" alt="Zephyr" style={{ width: 16, height: 16, borderRadius: 4, opacity: 0.18 }} />
        <span style={{ fontFamily: 'system-ui, sans-serif', fontSize: 10, fontWeight: 300, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.06)' }}>EPHYR</span>
      </div>
    </div>
  );
}
